/**
 * DivergencePanel
 *
 * Facilitator-facing surface that answers a single question the aggregate table
 * cannot answer at a glance: "Where are participants actually split, and which
 * disagreement is worst?"
 *
 * The aggregate table (AggregationView) sorts by score/result. This panel sorts
 * by *divergence* — worst disagreement first — so the facilitator can hold
 * divergence long enough to be useful (Canon 01 §2B, signal quality) instead of
 * letting the highest-spread items get smoothed over silently.
 *
 * This is a pure read/derive surface over data already computed by
 * `computeAggregates()` and persisted in `tool_session_responses`. It introduces
 * no new schema and no new write path — it makes existing signal visible and
 * actionable.
 *
 * Canon: 01 §2B (Signal quality — surface disagreement rather than smoothing it),
 *        00 §1D (Goodness Principle — a surface shaped to the facilitator's job).
 */

import { useMemo } from 'react';
import { AlertTriangle, Users, ArrowRight, ShieldCheck } from 'lucide-react';
import type { AggregateResult, ToolOutputType } from '../../../types/prioritization/toolSession';

// ── Divergence model ───────────────────────────────────────────────────────────

/**
 * Consensus at or above this level is treated as aligned — not surfaced as a
 * divergence the facilitator needs to act on.
 */
const ALIGNED_CONSENSUS = 0.7;
/** Below this consensus level, divergence is treated as severe (red). */
const SEVERE_CONSENSUS = 0.45;

export type DivergenceSeverity = 'severe' | 'moderate';

export interface DivergenceRow {
  itemId: string;
  itemTitle: string;
  /** 0–1, higher = more divergent (1 - consensusStrength). */
  divergence: number;
  severity: DivergenceSeverity;
  consensusStrength: number;
  contributorCount: number;
  /** Ranking/scoring only */
  averageScore?: number;
  scoreSpread?: number;
  scoreRange?: { min: number; max: number };
  scores?: number[];
  /** Classification only */
  categoryVotes?: Record<string, number>;
  distinctCategoryCount?: number;
}

const CATEGORY_LABELS: Record<string, string> = {
  ui: 'Do', in: 'Schedule', un: 'Delegate', nn: 'Eliminate',
  do: 'Do', schedule: 'Schedule', delegate: 'Delegate', eliminate: 'Eliminate',
  high: 'High', medium: 'Medium', low: 'Low',
};

function labelFor(category: string): string {
  return CATEGORY_LABELS[category.toLowerCase()] ?? category;
}

/**
 * Derive the ranked list of divergent items from raw aggregates.
 * Worst disagreement first. Items at/above ALIGNED_CONSENSUS are excluded.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function deriveDivergenceRows(
  aggregates: AggregateResult[],
  outputType: ToolOutputType,
): DivergenceRow[] {
  const rows: DivergenceRow[] = [];

  for (const agg of aggregates) {
    const contributorCount = agg.participantResults?.length ?? 0;
    // A single voice cannot diverge; need at least two participants.
    if (contributorCount < 2) continue;

    const consensus = agg.consensusStrength ?? 1;
    if (consensus >= ALIGNED_CONSENSUS) continue;

    const divergence = 1 - consensus;
    const severity: DivergenceSeverity =
      consensus < SEVERE_CONSENSUS ? 'severe' : 'moderate';

    const base: DivergenceRow = {
      itemId: agg.itemId,
      itemTitle: agg.itemTitle,
      divergence,
      severity,
      consensusStrength: consensus,
      contributorCount,
    };

    if (outputType === 'classification') {
      const votes = agg.categoryVotes ?? {};
      rows.push({
        ...base,
        categoryVotes: votes,
        distinctCategoryCount: Object.keys(votes).length,
      });
    } else {
      const scores = (agg.participantResults ?? [])
        .map(pr => pr.result.score)
        .filter((s): s is number => typeof s === 'number');
      rows.push({
        ...base,
        averageScore: agg.averageScore,
        scoreSpread: agg.scoreSpread,
        scoreRange: agg.scoreRange,
        scores,
      });
    }
  }

  // Worst first; tie-break by raw spread (ranking) then contributor count.
  return rows.sort((a, b) => {
    if (b.divergence !== a.divergence) return b.divergence - a.divergence;
    const spreadDiff = (b.scoreSpread ?? 0) - (a.scoreSpread ?? 0);
    if (spreadDiff !== 0) return spreadDiff;
    return b.contributorCount - a.contributorCount;
  });
}

// ── Sub-views ────────────────────────────────────────────────────────────────

function SeverityBadge({ severity }: { severity: DivergenceSeverity }) {
  const isSevere = severity === 'severe';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.65rem] font-semibold uppercase tracking-wide ${
        isSevere
          ? 'bg-[var(--coral-dim)] text-[var(--coral)]'
          : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
      }`}
    >
      {isSevere ? 'High split' : 'Some split'}
    </span>
  );
}

/** Dot plot of participant scores along the observed range. */
function ScoreSpreadStrip({ scores, range }: { scores: number[]; range?: { min: number; max: number } }) {
  if (scores.length === 0) return null;
  const min = range?.min ?? Math.min(...scores);
  const max = range?.max ?? Math.max(...scores);
  const span = max - min || 1;
  return (
    <div className="relative h-5 mt-1.5">
      <div className="absolute inset-x-0 top-1/2 h-px bg-[var(--hover-tint)]" />
      {scores.map((s, i) => {
        const pct = ((s - min) / span) * 100;
        return (
          <span
            key={i}
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[var(--slate)]/70 ring-2 ring-white"
            style={{ left: `${pct}%` }}
            title={`Score: ${s}`}
          />
        );
      })}
    </div>
  );
}

function VoteSplit({ votes }: { votes: Record<string, number> }) {
  const entries = Object.entries(votes).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((sum, [, n]) => sum + n, 0) || 1;
  return (
    <div className="mt-1.5 space-y-1">
      <div className="flex h-2 rounded-full overflow-hidden bg-[var(--hover-tint)]">
        {entries.map(([cat, n], i) => (
          <div
            key={cat}
            className={i === 0 ? 'bg-[var(--slate)]' : i === 1 ? 'bg-[var(--mustard)]' : 'bg-[var(--surface)]'}
            style={{ width: `${(n / total) * 100}%` }}
            title={`${labelFor(cat)}: ${n}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[0.7rem] text-[var(--text-ter)]">
        {entries.map(([cat, n]) => (
          <span key={cat}>
            <span className="font-medium text-[var(--text-sec)]">{labelFor(cat)}</span> · {n}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

interface DivergencePanelProps {
  aggregates: AggregateResult[];
  outputType: ToolOutputType;
  /** Called when the facilitator chooses to resolve an item (e.g. jump to its override row). */
  onResolve?: (itemId: string) => void;
  /** Total participants who have submitted — for the "X of Y split" framing. */
  participantCount?: number;
}

export function DivergencePanel({
  aggregates,
  outputType,
  onResolve,
  participantCount,
}: DivergencePanelProps) {
  const rows = useMemo(
    () => deriveDivergenceRows(aggregates, outputType),
    [aggregates, outputType],
  );

  // Fully aligned — affirm it rather than render an empty box.
  if (rows.length === 0) {
    const scorable = aggregates.filter(a => (a.participantResults?.length ?? 0) >= 2);
    if (scorable.length === 0) return null;
    return (
      <div className="border border-[var(--green)] bg-[var(--green-dim)] px-5 py-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[var(--green-dim)] flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="h-4 w-4 text-[var(--green)]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">No meaningful divergence</p>
          <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
            Participants are aligned across all items. No outliers need attention before you finalize.
          </p>
        </div>
      </div>
    );
  }

  const severeCount = rows.filter(r => r.severity === 'severe').length;

  return (
    <div className="border border-[var(--mustard)] bg-[var(--bg-raised)] overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 bg-[var(--mustard-dim)] border-b border-[var(--mustard)]">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[var(--mustard-dim)] flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-4 w-4 text-[var(--mustard)]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[var(--text-pri)]">
              Divergence — {rows.length} item{rows.length !== 1 ? 's' : ''} need a look
            </p>
            <p className="text-xs text-[var(--text-ter)]">
              Ranked by disagreement, worst first.
              {severeCount > 0 && (
                <span className="text-[var(--coral)] font-medium"> {severeCount} high-split.</span>
              )}
            </p>
          </div>
        </div>
      </div>

      <ul className="divide-y divide-[var(--rule)]">
        {rows.map(row => {
          const splitCount = row.contributorCount;
          return (
            <li key={row.itemId} className="px-5 py-3.5 hover:bg-[var(--surface)]/60 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[var(--text-pri)] truncate">{row.itemTitle}</span>
                    <SeverityBadge severity={row.severity} />
                  </div>

                  <div className="flex items-center gap-3 mt-1 text-xs text-[var(--text-ter)]">
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3 text-[var(--text-ter)]" />
                      {participantCount && participantCount !== splitCount
                        ? `${splitCount} of ${participantCount} weighed in`
                        : `${splitCount} participants`}
                    </span>
                    <span>·</span>
                    <span>{Math.round(row.consensusStrength * 100)}% consensus</span>
                    {row.averageScore !== undefined && (
                      <>
                        <span>·</span>
                        <span className="font-mono">
                          avg {row.averageScore.toFixed(1)}
                          {row.scoreSpread !== undefined && ` ±${row.scoreSpread.toFixed(1)}`}
                          {row.scoreRange && ` (${row.scoreRange.min}–${row.scoreRange.max})`}
                        </span>
                      </>
                    )}
                  </div>

                  {outputType === 'classification' && row.categoryVotes ? (
                    <VoteSplit votes={row.categoryVotes} />
                  ) : (
                    row.scores && <ScoreSpreadStrip scores={row.scores} range={row.scoreRange} />
                  )}
                </div>

                {onResolve && (
                  <button
                    onClick={() => onResolve(row.itemId)}
                    className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 border border-[var(--slate)] text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors"
                  >
                    Resolve
                    <ArrowRight className="h-3 w-3" />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default DivergencePanel;
